/**
 * Limites configuráveis da feature Assistente IA.
 *
 * Ficam centralizados aqui enquanto o backend não expõe essas regras. Quando a API
 * passar a devolver os limites (ex.: `maxLength` no ViewModel do prompt), troque o uso
 * do constante pelo valor do servidor em um único ponto — o hook correspondente.
 */

/** Tamanho máximo do prompt de persona ou de memória, em caracteres. Vale para cada um. */
export const PROMPT_MAX_LENGTH = 8000;

/**
 * Extensões aceitas no upload de documento (atributo HTML `accept`).
 *
 * Mais restrito que o servidor de propósito: `POST /api/v1/tutorai/documents/{classStudentId}`
 * também aceita `.pdf`, mas por ora só liberamos texto puro — a extração de PDF ainda não é
 * confiável o bastante para o contexto do tutor. Reabrir é acrescentar `.pdf` aqui; a
 * checagem de cliente do `useTutorDocumentUpload` e o `accept` do input derivam desta
 * constante, então o par de textos i18n (`upload.helper` e `documents.emptyDescription`) é
 * o único outro ponto a atualizar junto.
 */
export const CONTEXT_FILE_ACCEPT = '.md,.txt';

/** Tamanho máximo por documento, em megabytes. Regra de cliente — a API não declara limite. */
export const CONTEXT_FILE_MAX_SIZE_MB = 30;

/** Tamanho máximo por arquivo de contexto, em bytes. */
export const CONTEXT_FILE_MAX_SIZE_BYTES = CONTEXT_FILE_MAX_SIZE_MB * 1024 * 1024;

/** Janela default do dashboard de uso, em dias (contagem inclusiva). */
export const USAGE_DEFAULT_RANGE_DAYS = 30;

/**
 * Janela máxima que o filtro do dashboard de uso aceita, em dias (contagem inclusiva).
 *
 * Regra de produto: a consulta não recorta mais do que isso. Como é igual ao default, a
 * janela inicial já é a mais larga permitida — o usuário só estreita, nunca alarga.
 *
 * O teto também protege a UI: `buildUsageTimeline` densifica a série com um ponto por dia
 * do período, então um ano digitado errado no filtro (`01/01/0126` em vez de `01/01/2026`)
 * geraria centenas de milhares de pontos dentro de um `useMemo` de render, travando a aba.
 * `isPeriodValid` recusa o período antes de disparar a query, e `buildUsageTimeline`
 * repete a checagem porque é a fronteira que aloca.
 *
 * Invariante: `USAGE_DEFAULT_RANGE_DAYS` nunca pode exceder este valor, senão a janela
 * inicial da tela já nasce inválida.
 */
export const USAGE_MAX_RANGE_DAYS = 30;

/**
 * Teto de itens pedidos aos lookups de curso e turma (`Take`).
 *
 * Os lookups compartilhados não paginam — só aceitam `Take`. Como a lista é sempre
 * recortada por um texto de busca, este teto existe só para limitar o resultado de uma
 * busca muito genérica; o limite é explícito para não depender do default do servidor.
 */
export const LOOKUP_TAKE = 50;

/**
 * Mínimo de caracteres para disparar a busca nos lookups.
 *
 * Casa com o `getAutocompleteSearchMessage` compartilhado, que exibe "digite ao menos 3
 * caracteres" abaixo deste valor.
 */
export const LOOKUP_SEARCH_MIN_CHARS = 3;

/**
 * Debounce da busca dos lookups, em milissegundos.
 *
 * Segue o `useClassSearchDropdown`, que consome este mesmo endpoint de turmas. O filtro
 * de notificações usa 2000 ms, mas aquele hook faz paginação infinita com auto-fetch —
 * aqui a busca é uma única requisição, então o intervalo menor não gera tráfego extra.
 */
export const LOOKUP_SEARCH_DEBOUNCE_MS = 1000;
